import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Menu, X, ArrowRight, ChevronDown, Cpu, Bot, BarChart3, Shield, Database } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { useTranslation } from 'react-i18next';

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductOpen, setIsProductOpen] = useState(false);
  const productRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const isFrench = location.pathname.startsWith('/fr');
  if (isFrench && i18n.language !== 'fr') {
    i18n.changeLanguage('fr');
  } else if (!isFrench && i18n.language !== 'en') {
    i18n.changeLanguage('en');
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (productRef.current && !productRef.current.contains(e.target as Node)) {
        setIsProductOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) => {
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/fr/, '');
    return pathWithoutLang === path || currentPath === path;
  };

  const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
    <Link
      to={to}
      className={cn(
        "px-3 py-2 text-[13px] font-medium transition-colors duration-200",
        isActive(to)
          ? "text-foreground underline underline-offset-8"
          : "text-muted-foreground hover:text-foreground"
      )}
      onClick={() => { setIsMenuOpen(false); setIsProductOpen(false); }}
    >
      {children}
    </Link>
  );

  const productLinks = [
    { to: isFrench ? '/fr/product/platform' : '/product/platform', icon: Cpu, label: 'Core Platform', desc: 'AI Assistant, Meeting Intel, Pipeline Intelligence' },
    { to: '/ai-sdr', icon: Bot, label: 'Autonomous AI SDR', desc: 'Books meetings for your AEs, sends invite, briefs in Slack' },
    { to: isFrench ? '/fr/product/command-center' : '/product/command-center', icon: BarChart3, label: 'Command Center', desc: "The CRO's War Room" },
    { to: '/crm-optional', icon: Database, label: 'CRM-optional · Early access', desc: 'Run standalone with a Google Drive mirror. Add HubSpot or Salesforce later.' },

  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-foreground/20 bg-background/95 backdrop-blur-xl supports-[backdrop-filter]:bg-background/85">
      <div className="container flex h-[72px] items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="flex items-center">
            <span className="text-xl font-display font-bold text-foreground">
              NoQ5<span className="font-sans font-medium">.ai</span>
            </span>
          </motion.div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          <NavLink to={isFrench ? '/fr' : '/'}>{t('nav.home')}</NavLink>

          {/* Product Dropdown */}
          <div ref={productRef} className="relative">
            <button
              className={cn(
                "px-3 py-2 text-[13px] font-medium transition-colors duration-200 flex items-center gap-1",
                location.pathname.includes('/product')
                  ? "text-foreground underline underline-offset-8"
                  : "text-muted-foreground hover:text-foreground"
              )}
              onClick={() => setIsProductOpen(!isProductOpen)}
              aria-expanded={isProductOpen}
            >
              Product
              <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", isProductOpen && "rotate-180")} />
            </button>

            <AnimatePresence>
              {isProductOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.96 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 mt-3 w-[360px] bg-card border border-foreground/20 rounded-sm shadow-lg p-2 z-50"
                >
                  {productLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsProductOpen(false)}
                      className="flex items-start gap-3 p-3 rounded-sm hover:bg-muted transition-colors group"
                    >
                      <div className="mt-0.5 p-2 border border-foreground/20 text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <item.icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{item.label}</p>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                    </Link>
                  ))}
                  <div className="border-t border-border mt-2 pt-2">
                    <Link
                      to={isFrench ? '/fr/integrations' : '/integrations'}
                      onClick={() => setIsProductOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-colors"
                    >
                      <Shield className="w-4 h-4" />
                      Integrations & Security
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NavLink to="/use-cases">Use Cases</NavLink>
          <NavLink to={isFrench ? '/fr/tarifs' : '/pricing'}>{t('nav.pricing')}</NavLink>
          <NavLink to={isFrench ? '/fr/blog' : '/blog'}>{t('nav.blog')}</NavLink>
          <NavLink to={isFrench ? '/fr/a-propos' : '/about'}>{t('footer.aboutUs')}</NavLink>
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-3">
          
          <Button variant="ghost" className="hidden sm:inline-flex text-muted-foreground hover:text-foreground" asChild>
            <a href="https://app.noq5.ai/" target="_blank" rel="noopener noreferrer">{t('nav.login')}</a>
          </Button>
          <Button className="hidden sm:inline-flex font-semibold group" asChild>
            <Link to="/?scrollTo=demo">
              Contact Us
              <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </Button>
          <button
            className="md:hidden p-2 rounded-sm hover:bg-muted transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="md:hidden fixed top-16 left-0 right-0 bg-background border-b border-border shadow-lg z-50 max-h-[calc(100vh-4rem)] overflow-y-auto"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <nav className="container py-4 flex flex-col gap-1">
              <NavLink to={isFrench ? '/fr' : '/'}>{t('nav.home')}</NavLink>
              <p className="px-4 pt-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Product</p>
              {productLinks.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-muted transition-colors"
                >
                  <item.icon className="w-4 h-4 text-primary" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                </Link>
              ))}
              <NavLink to="/use-cases">Use Cases</NavLink>
              <NavLink to={isFrench ? '/fr/integrations' : '/integrations'}>Integrations</NavLink>
              <NavLink to={isFrench ? '/fr/tarifs' : '/pricing'}>{t('nav.pricing')}</NavLink>
              <NavLink to={isFrench ? '/fr/blog' : '/blog'}>{t('nav.blog')}</NavLink>
              <NavLink to={isFrench ? '/fr/a-propos' : '/about'}>{t('footer.aboutUs')}</NavLink>

              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-border">
                <a href="https://app.noq5.ai/" target="_blank" rel="noopener noreferrer"
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setIsMenuOpen(false)}>{t('nav.login')}</a>
                <Link to="/?scrollTo=demo"
                  className="px-4 py-3 bg-primary text-primary-foreground text-center font-medium rounded-lg shadow-button"
                  onClick={() => setIsMenuOpen(false)}>Contact Us</Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
